import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import { InquiryDialogProvider } from "@/context/InquiryDialogContext";
import { publicApi } from "@/lib/api";
import ErrorBoundary from "@/components/ErrorBoundary";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import InquiryFormDialog from "@/components/InquiryFormDialog";
import ProtectedRoute from "@/components/admin/ProtectedRoute";
import PageLoader from "@/components/PageLoader";
import CanonicalManager from "@/components/CanonicalManager";
import ScrollManager from "@/components/ScrollManager";
import HomePage from "@/pages/HomePage";

// Route-level code splitting: everything below is loaded on-demand (separate JS
// chunks) instead of being bundled into the initial page load. HomePage stays a
// regular (eager) import above since it's what almost every visitor hits first -
// splitting it would only add a loading flicker with no real benefit.
//
// This matters most for:
// - Admin pages (`/yonetim/*`): pull in recharts (visit charts) and @dnd-kit (photo/
//   showcase reordering) - together ~1MB+ of source that a public visitor never needs.
// - ListingDetailPage: the only public page that renders the Leaflet map
//   (react-leaflet + leaflet, ~500KB of source). Home/About/Vision/Category never
//   need to pay that cost.
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const VisionPage = lazy(() => import("@/pages/VisionPage"));
const CategoryPage = lazy(() => import("@/pages/CategoryPage"));
const ListingDetailPage = lazy(() => import("@/pages/ListingDetailPage"));
const AdminLoginPage = lazy(() => import("@/pages/admin/AdminLoginPage"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminListingsPage = lazy(() => import("@/pages/admin/AdminListingsPage"));
const AdminListingFormPage = lazy(() => import("@/pages/admin/AdminListingFormPage"));
const AdminInquiriesPage = lazy(() => import("@/pages/admin/AdminInquiriesPage"));
const AdminTeamPage = lazy(() => import("@/pages/admin/AdminTeamPage"));
const AdminSettingsPage = lazy(() => import("@/pages/admin/AdminSettingsPage"));
const AdminShowcasePage = lazy(() => import("@/pages/admin/AdminShowcasePage"));

const PublicLayout = ({ children }) => (
  <div className="min-h-screen flex flex-col">
    <Navbar />
    <main className="flex-1">{children}</main>
    <Footer />
    <InquiryFormDialog />
  </div>
);

/**
 * Direct-URL / F5-refresh alias so `https://.../admin`, `/admin/giris`,
 * `/admin/ilanlar/yeni`, etc. all resolve to the equivalent `/yonetim...` route
 * instead of 404-ing. The canonical admin path remains `/yonetim` (used by every
 * in-app link); this only helps anyone who bookmarks/types `/admin` directly.
 */
const AdminAliasRedirect = () => {
  const location = useLocation();
  const suffix = location.pathname.replace(/^\/admin/, "");
  return <Navigate to={`/yonetim${suffix}${location.search}`} replace />;
};

/**
 * Faz 3: lightweight client-side safety net for the clean share bridge
 * (`/paylas/:id`, see ShareListingButton). The real, crawler-visible Open Graph
 * HTML for that path is server-rendered by the BACKEND (see
 * `_render_listing_social_html` / `clean_share_listing_preview` in server.py) -
 * reaching the backend from the public main domain requires a one-time production
 * routing rule (see Faz 3 report). If a human ever lands on this SPA route instead
 * (routing rule not yet configured, or a crawler-less direct visit), this simply
 * redirects straight to the real listing page rather than showing a dead/blank
 * route - never a replacement for the server-rendered metadata itself.
 */
const PaylasRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/ilan/${id}`} replace />;
};

function App() {
  useEffect(() => {
    // Genel ziyaretçi analitiği (sadece yönetici paneli Gösterge Paneli'nde görünür).
    publicApi.post("/track-visit").catch(() => {});
  }, []);

  return (
    <ErrorBoundary message="Uygulama yüklenirken beklenmeyen bir sorun oluştu. Sayfayı yenilemeyi deneyin.">
      <AdminAuthProvider>
        <InquiryDialogProvider>
          <BrowserRouter>
            <CanonicalManager />
            <ScrollManager />
            <Toaster position="top-center" richColors closeButton />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route
                  path="/"
                  element={
                    <PublicLayout>
                      <HomePage />
                    </PublicLayout>
                  }
                />
                <Route
                  path="/hakkimizda"
                  element={
                    <PublicLayout>
                      <AboutPage />
                    </PublicLayout>
                  }
                />
                <Route
                  path="/vizyon-misyon"
                  element={
                    <PublicLayout>
                      <VisionPage />
                    </PublicLayout>
                  }
                />
                <Route
                  path="/kategori/:category"
                  element={
                    <PublicLayout>
                      <CategoryPage />
                    </PublicLayout>
                  }
                />
                <Route
                  path="/ilan/:id"
                  element={
                    <PublicLayout>
                      <ListingDetailPage />
                    </PublicLayout>
                  }
                />
                <Route path="/yonetim/giris" element={<AdminLoginPage />} />
                <Route
                  path="/yonetim"
                  element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<AdminListingsPage />} />
                  <Route path="ilanlar/yeni" element={<AdminListingFormPage />} />
                  <Route path="ilanlar/:id/duzenle" element={<AdminListingFormPage />} />
                  <Route path="vitrin" element={<AdminShowcasePage />} />
                  <Route path="basvurular" element={<AdminInquiriesPage />} />
                  <Route path="ekip" element={<AdminTeamPage />} />
                  <Route path="ayarlar" element={<AdminSettingsPage />} />
                </Route>
                {/* /admin alias -> /yonetim (bookmark/direct-URL/refresh friendly) */}
                <Route path="/admin" element={<AdminAliasRedirect />} />
                <Route path="/admin/*" element={<AdminAliasRedirect />} />
                {/* Faz 3: clean share bridge safety net - see PaylasRedirect above */}
                <Route path="/paylas/:id" element={<PaylasRedirect />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </InquiryDialogProvider>
      </AdminAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
